import React, { ChangeEvent, useState, useEffect } from "react";
import styles from "./style.module.scss";
// import { SplitFrame } from "types";
import { frontColours, rearColours } from "../../../../assets/tyreColours";
import ScatterTimeChart from "../ScatterTimeChart";
import { useSelector } from "react-redux";
import { RootState, store } from "../../../../state";
import { useDispatch } from "react-redux";

import {
  Chart as ChartJS,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import zoomPlugin from "chartjs-plugin-zoom";

import "chartjs-adapter-dayjs-4/dist/chartjs-adapter-dayjs-4.esm";
import { FrameType0, FrameType1, SplitFrame } from "../../../../types";
import {
  setSelectedPlot,
  setShowPrediction,
} from "../../../../state/tpmsReducer";
import { OneSeriesObject } from "../../../../types";

ChartJS.register(
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
);

interface TyreGroupChartsProps {
  group: string;
}

interface PlotData {
  x: number;
  y: number | null;
}

export default function TyreGroupCharts(props: TyreGroupChartsProps) {
  const { group } = props;
  const pWarning = useSelector(
    (state: RootState) => state.tpms.tpmsConfig?.pWarning,
  );
  const pAtm =
    useSelector((state: RootState) => state.tpms.tpmsConfig?.pAtm) || null;
  const kHeatTransferAir =
    useSelector(
      (state: RootState) => state.tpms.tpmsConfig?.kHeatTransferAir,
    ) || 0;
  const kHeatTransferTyre =
    useSelector(
      (state: RootState) => state.tpms.tpmsConfig?.kHeatTransferTyre,
    ) || 0;
  const tGarage =
    useSelector((state: RootState) => state.tpms.tpmsConfig?.tGarage) || 0;
  const selectedPlot = useSelector(
    (state: RootState) => state.tpms.selectedPlot,
  );
  const showPrediction = useSelector(
    (state: RootState) => state.tpms.showPrediction,
  );

  // const currentTime = new Date("2024-12-07T18:30:00.000+00:00").getTime();
  const [currentTime, setTime] = useState(Date.now());
  useEffect(() => {
    const interval = setInterval(() => setTime(Date.now()), 60 * 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  const defaultXMin = currentTime - 175 * 60 * 1000;
  let defaultXMax = currentTime + 5 * 60 * 1000;
  if (showPrediction) {
    defaultXMax += 2 * 60 * 60 * 1000;
  }

  const data =
    group === "Front"
      ? store.getState().tpms.allTyreData.front
      : store.getState().tpms.allTyreData.rear;

  const colours = group === "Front" ? frontColours : rearColours;

  // let minimumTime = currentTime;
  // let maximumTime = currentTime;
  // chartData.datasets.map((x: any) => {
  //   if (x.data.length > 0) {
  //     const dtMin: number = x.data[0].x;
  //     const dtMax: number = x.data[x.data.length - 1].x;
  //     if (!minimumTime || dtMin < minimumTime) {
  //       minimumTime = dtMin;
  //     }
  //     if (!maximumTime || dtMax > maximumTime) {
  //       maximumTime = dtMax;
  //     }
  //   }
  // });

  function greaterThanIdx(
    ctxSegment: { p0DataIndex: number },
    idx: number,
    value: number | number[],
  ): unknown {
    if (ctxSegment.p0DataIndex > idx) {
      return value;
    } else {
      return undefined;
    }
  }

  const pressurePlotData = {
    datasets: data.map((tyre: SplitFrame, index: number) => {
      const data = tyre.frameType0.map((obj: FrameType0) => {
        const a: PlotData = {
          x: obj.dt,
          y: pAtm ? Math.round((obj.pAbs / 1000 - pAtm) * 1000) / 1000 : null,
        };
        return a;
      });

      return {
        label: tyre.tyreId,
        data: data,
        backgroundColor: colours[index],
        borderColor: colours[index],
        borderWidth: 2,
        pointRadius: 1,
        showLine: true,
      };
    }),
  };

  const temperaturePlotData = {
    datasets: data.map((tyre: SplitFrame, index: number) => {
      const data = tyre.frameType1.map((obj: FrameType1) => {
        const avgIR =
          obj.TIR2 && obj.TIR3 && obj.TIR4
            ? (obj.TIR2 + obj.TIR3 + obj.TIR4) / 3
            : obj.TIR3;
        const a: PlotData = {
          x: obj.dt,
          y: selectedPlot === "air" ? obj.TAir : avgIR,
        };
        return a;
      });

      const startOfPredictionIdx = data.length;
      if (showPrediction) {
        if (data.length > 0) {
          const lastIndex = data.map((x) => x.y !== null).lastIndexOf(true);
          if (lastIndex !== -1) {
            const dt = data[lastIndex].x;
            const T = data[lastIndex].y;
            if (T) {
              for (let i = 1; i <= 300; i += 5) {
                const k =
                  selectedPlot === "air" ? kHeatTransferAir : kHeatTransferTyre;
                const x = dt + i * 60 * 1000;
                const y = tGarage + (T - tGarage) * Math.exp(-k * i * 60);
                data.push({ x: x, y: y });
              }
            }
          }
        }
      }

      const pointRadiusArray = Array(data.length).fill(0);
      pointRadiusArray.fill(1, 0, startOfPredictionIdx);
      return {
        label: tyre.tyreId,
        data: data,
        backgroundColor: colours[index],
        borderColor: colours[index],
        borderWidth: 2,
        pointRadius: pointRadiusArray,
        showLine: true,
        segment: {
          borderDash: (ctx: { p0DataIndex: number }) =>
            greaterThanIdx(ctx, startOfPredictionIdx + 1, [5, 5]),
          borderWidth: (ctx: { p0DataIndex: number }) =>
            greaterThanIdx(ctx, startOfPredictionIdx + 1, 0.75),
        },
      };
    }),
  };

  const TIME_GAP_T0_BREAK = 300; //5 min
  for (const plot of [pressurePlotData, temperaturePlotData]) {
    for (const ds in plot.datasets) {
      let lastDt = null;
      const breakIdxs = [];
      for (let i = 0; i < plot.datasets[ds].data.length; i++) {
        const dt = plot.datasets[ds].data[i].x;
        if (lastDt && dt - lastDt > TIME_GAP_T0_BREAK * 1000) {
          breakIdxs.push({ idx: i, dt: dt });
        }
        lastDt = dt;
      }
      for (let i = 0; i < breakIdxs.length; i++) {
        const idx = breakIdxs[i].idx;
        const dt = breakIdxs[i].dt - 1;
        plot.datasets[ds].data.splice(idx + i, 0, {
          x: dt,
          y: null,
        });
      }
    }
  }

  // const chartRef = React.useRef(null);
  // const chartRef2 = React.useRef(null);
  // const resetZoom = () => {
  //   if (chartRef && chartRef.current) {
  //     const ref = chartRef.current as any;
  //     // ref.options.scales.x.min = Date.now() - 24 * 60 * 60;
  //     // ref.options.scales.x.max = Date.now();
  //     ref.options.scales.y.min = null;
  //     ref.options.scales.y.max = null;
  //     ref.update();
  //   }
  //   if (chartRef2 && chartRef2.current) {
  //     const ref2 = chartRef2.current as any;
  //     // ref2.options.scales.x.min = Date.now() - 24 * 60 * 60;
  //     // ref2.options.scales.x.max = Date.now();
  //     ref2.options.scales.y.min = null;
  //     ref2.options.scales.y.max = null;
  //     ref2.update();
  //   }
  // };

  const dispatch = useDispatch();
  const handleOptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedPlot = event.target.value;
    dispatch(setSelectedPlot(selectedPlot));
  };

  const toggleShowPrediction = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    dispatch(setShowPrediction(value));
  };

  const pressureYMin = useSelector(
    (state: RootState) => state.tpms.pressureYMin,
  );
  const pressureYMax = useSelector(
    (state: RootState) => state.tpms.pressureYMax,
  );
  const tempYMin = useSelector((state: RootState) => state.tpms.tempYMin);
  const tempYMax = useSelector((state: RootState) => state.tpms.tempYMax);
  const xAxisMin = useSelector((state: RootState) => state.tpms.xAxisMin);
  const xAxisMax = useSelector((state: RootState) => state.tpms.xAxisMax);
  const xMin = xAxisMin !== undefined ? xAxisMin : defaultXMin;
  const xMax = xAxisMax !== undefined ? xAxisMax : defaultXMax;

  const isTemperatureEmpty = !temperaturePlotData.datasets.some(
    (x: OneSeriesObject) => x.data.length > 0,
  );
  return (
    <div className={styles.container}>
      {!isTemperatureEmpty ? (
        <>
          <div className={`${styles.showPredictionContainer}`}>
            <div>Prediction:</div>
            <input
              type="checkbox"
              checked={showPrediction}
              onChange={toggleShowPrediction}
            ></input>
          </div>
          <form className={styles.plotSelectionForm}>
            <label className={styles.plotSelectionLabel}>
              <input
                onChange={handleOptionChange}
                type="radio"
                value="air"
                name="car"
                checked={selectedPlot === "air"}
              />
              Air
            </label>
            <label className={styles.plotSelectionLabel}>
              <input
                onChange={handleOptionChange}
                type="radio"
                value="tyre"
                name="car"
                checked={selectedPlot === "tyre"}
              />
              Tyre
            </label>
          </form>
        </>
      ) : (
        <></>
      )}
      <div className={styles.oneChart}>
        <ScatterTimeChart
          chartData={pressurePlotData}
          chartTitle="Tyre Pressure"
          yTitle="Pressure (bar)"
          xLimits={[xMin, xMax]}
          yLimits={[pressureYMin, pressureYMax]}
          ySteps={0.05}
          yZoomLimits={[0, 2.5]}
          warningLine={pWarning ? pWarning : null}
        ></ScatterTimeChart>
      </div>
      <div className={styles.oneChart}>
        <ScatterTimeChart
          chartData={temperaturePlotData}
          chartTitle={
            selectedPlot === "air" ? "Air Temperature" : "Tyre Temperature"
          }
          yLimits={[tempYMin, tempYMax]}
          yTitle="Temperature (°C)"
          xLimits={[xMin, xMax]}
          ySteps={1}
          yZoomLimits={[0, 50]}
          warningLine={null}
        ></ScatterTimeChart>
      </div>
    </div>
  );
}
